import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, PanelLeftClose, PanelLeftOpen, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { useToast } from '@/components/ui/use-toast';
import NewRoundDialog from '../components/NewRoundDialog';
import { competitionRoundsService } from '../../services/competition-rounds';
import { useGymAdmin } from '../context/gym-admin-context';
import { refreshCompetitionRounds, useCompetitionRounds } from '../hooks/useCompetitionRounds';
import { allowedNavSections, type GymAdminNavItem } from '../nav';
import { GymSwitcher } from './GymSwitcher';
import { NavUser } from './NavUser';
import type { CompetitionRound, CreateRoundData } from '../../types/competition-round';

/**
 * Shrinks the sidebar to the icon rail and back. The rail edge and the header
 * trigger do the same thing, but neither says so — this one is in the menu it
 * acts on, which is where it gets looked for.
 */
function CollapseToggle() {
  const { t } = useTranslation();
  const { state, toggleSidebar, isMobile } = useSidebar();

  // On mobile the sidebar is a drawer, not a rail: there is nothing to shrink.
  if (isMobile) return null;

  const collapsed = state === 'collapsed';
  const label = collapsed ? t('gymAdmin.nav.expand') : t('gymAdmin.nav.collapse');
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton onClick={toggleSidebar} tooltip={label} data-testid="sidebar-collapse">
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * Whether the URL is on this round — its board, or the setup behind it. Both
 * mark the same row, so the menu always says which round is open.
 */
function onRound(sectionPath: string, roundId: string): boolean {
  return sectionPath === `/rounds/${roundId}` || sectionPath.startsWith(`/rounds/${roundId}/`);
}

/**
 * The rounds entry: the competition's rounds, the `+` that makes one and the bin
 * that unmakes one.
 *
 * Records rather than sections, so they are fetched (see `useCompetitionRounds`)
 * rather than declared in `nav.ts`. Each row opens that round's scoreboard,
 * which is what the desk and whoever runs the competition spend the day in; the
 * round's own page is where it is set up.
 *
 * **Making and unmaking a round live here** rather than on a page of their own.
 * A round is only ever a row in this menu — there is nothing to say about the
 * list of them that the list itself does not say — so the two things one does to
 * the list are on the list: `+` beside the header, and a bin on the row.
 *
 * Nothing is drawn under the entry for a competition with no rounds yet — a lone
 * header over nothing reads as a menu that failed to load.
 */
function RoundsMenuItem({ item }: { item: GymAdminNavItem }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { placePath, sectionPath, selectedGymSlug } = useGymAdmin();
  const rounds = useCompetitionRounds(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<CompetitionRound | null>(null);
  const [busy, setBusy] = useState(false);
  const Icon = item.icon;

  /**
   * Makes it, and goes straight to its setup: a new round runs no classes and
   * has no blocks, so there is nothing to score on it until somebody says what
   * it is made of.
   */
  const handleCreate = async (data: CreateRoundData) => {
    if (!selectedGymSlug) throw new Error('no competition selected');

    try {
      const created = await competitionRoundsService.createRound(selectedGymSlug, data);
      refreshCompetitionRounds();
      navigate(placePath(`/rounds/${created.id}/edit`));
      return created;
    } catch (error) {
      toast({
        title: t('gymAdmin.rounds.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      // Back to the dialog, still filled in with what was typed.
      throw error;
    }
  };

  const handleDelete = async (round: CompetitionRound) => {
    if (!selectedGymSlug) return;

    setBusy(true);
    try {
      await competitionRoundsService.deleteRound(selectedGymSlug, round.id);
      refreshCompetitionRounds();
      setDeleting(null);
      // Standing on the round that has just gone: the entry decides where to go
      // from here — another round, or the page that says there are none.
      if (onRound(sectionPath, round.id)) navigate(placePath('/rounds'));
    } catch (error) {
      toast({
        title: t('gymAdmin.rounds.deleteError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={sectionPath === item.path}
        tooltip={t(item.labelKey)}
      >
        <Link to={placePath(item.path)}>
          <Icon />
          <span>{t(item.labelKey)}</span>
        </Link>
      </SidebarMenuButton>

      <SidebarMenuAction
        onClick={() => setCreating(true)}
        title={t('gymAdmin.rounds.new')}
        aria-label={t('gymAdmin.rounds.new')}
        data-testid="new-round"
      >
        <Plus />
      </SidebarMenuAction>

      {rounds.length > 0 && (
        <SidebarMenuSub data-testid="rounds-submenu">
          {rounds.map((round) => (
            <SidebarMenuSubItem key={round.id} className="group/round-row relative">
              <SidebarMenuSubButton asChild isActive={onRound(sectionPath, round.id)} className="pr-7">
                <Link to={placePath(`/rounds/${round.id}`)}>
                  <span>{round.name}</span>
                </Link>
              </SidebarMenuSubButton>
              {/* On the row it would delete, and only on the row the pointer is
                  on — a column of bins down the menu is an invitation nobody
                  asked for. Always out on a touch screen, which has no hover.
                  A running round is refused by the server, so it is not offered
                  here either — stopping it first is the answer. */}
              <SidebarMenuAction
                className="top-1 opacity-0 transition-opacity focus-visible:opacity-100 group-focus-within/round-row:opacity-100 group-hover/round-row:opacity-100 max-md:opacity-100 disabled:cursor-not-allowed disabled:text-muted-foreground"
                disabled={round.running || busy}
                onClick={() => setDeleting(round)}
                title={t('gymAdmin.rounds.delete')}
                aria-label={t('gymAdmin.rounds.delete')}
                data-testid={`delete-round-${round.id}`}
              >
                <Trash2 />
              </SidebarMenuAction>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}

      <NewRoundDialog open={creating} onOpenChange={setCreating} onCreate={handleCreate} />

      {/* Asked every time, and it says what goes: a round takes its problems and
          every result judged on them with it, and none of that comes back. */}
      <Dialog open={deleting !== null} onOpenChange={(next) => !next && setDeleting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('gymAdmin.rounds.delete')}</DialogTitle>
            <DialogDescription>
              {deleting && deleting.results_count > 0
                ? t('gymAdmin.rounds.confirmDeleteWithResults', {
                    name: deleting.name,
                    count: deleting.results_count,
                  })
                : t('gymAdmin.rounds.confirmDelete', { name: deleting?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => deleting && handleDelete(deleting)}
              data-testid="confirm-delete-round"
            >
              {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('gymAdmin.rounds.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const { t } = useTranslation();
  const { roles, locationType, place, placePath, sectionPath } = useGymAdmin();

  const sections = allowedNavSections(roles, locationType, place);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <GymSwitcher />
      </SidebarHeader>

      {/* One group per section, headed by what its entries have in common. In
          icon mode the headers fade out and the icons stay, which is why the
          sections are groups rather than folding sub-menus. */}
      <SidebarContent>
        {sections.map((section) => (
          <SidebarGroup key={section.id}>
            <SidebarGroupLabel>{t(section.labelKey)}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const Icon = item.icon;

                  // The rounds entry carries its own records and the buttons
                  // that make and unmake them, so it draws itself.
                  if (item.subMenu === 'rounds') {
                    return <RoundsMenuItem key={item.path} item={item} />;
                  }

                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        asChild
                        isActive={sectionPath === item.path}
                        tooltip={t(item.labelKey)}
                      >
                        <Link to={placePath(item.path)}>
                          <Icon />
                          <span>{t(item.labelKey)}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <CollapseToggle />
          <NavUser />
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
